import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { IPaginationData } from '../../../api/base';
import moment from 'moment';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { PostItemImage } from '../../styled';
import { toast } from 'react-toastify';
import companyApi from '../../../api/company.api';
import { ECompanyType, ICompany } from '../../../models/company.model';
import _ from 'lodash';

export default function PortfolioListPage() {
  const [dataSource, setDataSource] = useState<IPaginationData<ICompany>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [loading, setLoading] = useState(false);
  const [[sortBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >([undefined, 'asc']);

  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    companyApi
      .getList({
        ...filter,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: sortBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sortBy, order, filter]);

  useEffect(loadData, [loadData]);

  const theme = useTheme();

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Portfolio</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <Link to="create">
            <Button variant="contained" color="success" startIcon={<Add />}>
              Create new
            </Button>
          </Link>
        </div>
      </Box>

      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={sortBy}
        loading={loading}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'order',
            title: 'Order',
            dataKey: 'order',
            sortable: true,
          },
          {
            key: 'logo_url',
            title: 'Logo',
            dataKey: 'logo_url',
            render: (data) =>
              data.logo_url ? (
                <PostItemImage
                  src={process.env.REACT_APP_API_URI + data.logo_url}
                />
              ) : null,
          },
          {
            key: 'name',
            title: 'Name',
            dataKey: '',
            render: (data) => (
              <>
                <div>{data.name_vi}</div>
                <div>{data.name_en}</div>
              </>
            ),
          },
          {
            key: 'founder',
            title: 'Founder',
            dataKey: '',
            render: (data) => (
              <>
                <div>{data.founder_vi}</div>
                <div>{data.founder_en}</div>
              </>
            ),
          },
          {
            key: 'type',
            title: 'Type',
            dataKey: 'type',
            sortable: true,
            render: (data) => _.startCase(data.type),
          },
          {
            key: 'country',
            title: 'Country',
            dataKey: '',
            render: (data) =>
              data.country
                ? `${data.country?.name_vi} (${data.country?.name_en})`
                : null,
          },
          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            key: 'hidden',
            title: 'Hidden',
            dataKey: 'hidden',
            sortable: true,
            render: (data) => (
              <Checkbox
                onChange={(ev, value) => {
                  const idx = dataSource.records.indexOf(data);
                  companyApi.update(data.id, { hidden: value }).then((res) => {
                    const records = Array.from(dataSource.records);
                    records[idx] = {
                      ...data,
                      hidden: res.data.hidden,
                    };
                    setDataSource({
                      ...dataSource,
                      records,
                    });
                  });
                }}
                checked={data.hidden}
                readOnly
              />
            ),
          },
          {
            key: 'action',
            title: '',
            dataKey: 'id',
            render: (data) => (
              <>
                <Link to={data.id}>
                  <IconButton>
                    <Edit />
                  </IconButton>
                </Link>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />
      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this company?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                companyApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete company successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
